import { type ReactNode, useState } from "react";

type FAQItem = {
	question: string;
	answer: ReactNode;
};

const FAQ_ITEMS: FAQItem[] = [
	{
		question: "How does Squash help?",
		answer: (
			<>
				We turn sprawling multi-channel chatter into a prioritized feed of the
				highest-value actions - feature gaps, bugs, doc fixes, and promising
				users to onboard; so your team always knows what moves adoption fastest.
			</>
		),
	},
	{
		question: "What does onboarding look like?",
		answer: (
			<>
				We work alongside you during setup. Install the Squash GitHub app and
				Discord bot, and we&apos;ll ensure priority signals flow on day one.
			</>
		),
	},
	{
		question: "How do you handle data?",
		answer: (
			<>
				Squash only processes public Discord data on the communities you
				connect, and we make full deletion simple. We&apos;re happy to
				collaborate on any GDPR or privacy workflows your team needs.
			</>
		),
	},
	{
		question: "How is this different from our existing tooling?",
		answer: (
			<>
				Squash automates the triage and prioritization your team usually does
				manually in spreadsheets, CRMs, or notes, so you get straight to the
				highest-impact actions without combing through every thread.
			</>
		),
	},
];

function CollapsibleFaqItem({
	question,
	answer,
	itemId,
}: FAQItem & { itemId: string }) {
	const [isOpen, setIsOpen] = useState(false);

	return (
		<div className="glass-panel rounded-3xl border border-white/50 bg-white/70 p-5 shadow-float backdrop-blur-xl sm:p-6">
			<button
				type="button"
				className="flex w-full items-center justify-between gap-3 text-left"
				aria-expanded={isOpen}
				aria-controls={`${itemId}-content`}
				onClick={() => setIsOpen((prev) => !prev)}
			>
				<span className="text-base font-semibold text-foreground sm:text-lg">
					{question}
				</span>
				<span className="flex h-8 w-8 flex-none items-center justify-center rounded-full border border-white/50 bg-white/70 text-sm font-semibold text-primary shadow-sm">
					{isOpen ? "-" : "+"}
				</span>
			</button>
			<div
				id={`${itemId}-content`}
				className={`mt-2 text-sm text-muted-foreground transition-all duration-200 ease-out sm:text-base ${
					isOpen ? "block" : "hidden"
				}`}
			>
				{answer}
			</div>
		</div>
	);
}

export function FAQ() {
	return (
		<section id="faq" className="relative overflow-hidden py-20 sm:py-24">
			<div className="pointer-events-none absolute inset-0 -z-10 bg-[radial-gradient(circle_at_center,_rgba(210,244,238,0.45),_transparent_70%)]" />
			<div className="container mx-auto px-4 sm:px-6 lg:px-8">
				<div className="mx-auto max-w-3xl text-center">
					<h2 className="text-3xl sm:text-4xl">Frequently Asked Questions</h2>
				</div>

				<div className="mx-auto mt-12 max-w-4xl space-y-3">
					{FAQ_ITEMS.map((item, index) => (
						<CollapsibleFaqItem
							key={item.question}
							itemId={`faq-item-${index}`}
							{...item}
						/>
					))}
				</div>
			</div>
		</section>
	);
}
